import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import AvatarIcon from "./AvatarIcon";
import { tokens } from "../theme";

type PopularQuestionKey =
  | "popular.q1"
  | "popular.q2"
  | "popular.q3"
  | "popular.q4";

export interface PopularQuestion {
  id: string;
  labelKey: PopularQuestionKey;
  icon: "robot" | "sparkles" | "target" | "rocket";
}

const QUESTIONS: PopularQuestion[] = [
  { id: "q1", labelKey: "popular.q1", icon: "robot" },
  { id: "q2", labelKey: "popular.q2", icon: "sparkles" },
  { id: "q3", labelKey: "popular.q3", icon: "target" },
  { id: "q4", labelKey: "popular.q4", icon: "rocket" },
];

interface PopularQuestionsProps {
  onSelectQuestion: (question: string) => void;
  disabled?: boolean;
}

const styles: Record<string, CSSProperties> = {
  section: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginTop: tokens.space[2],
  },
  greeting: {
    margin: `0 0 ${tokens.space[3]}`,
    fontSize: tokens.font.size.xl,
    fontWeight: tokens.font.weight.bold,
    color: tokens.text.primary,
    letterSpacing: "-0.02em",
    lineHeight: tokens.font.lineHeight.tight,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.space[2],
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: tokens.space[3],
    width: "100%",
    textAlign: "left",
    padding: `${tokens.space[3]} 14px`,
    background: tokens.surface.chip,
    border: `1px solid ${tokens.border.default}`,
    borderRadius: tokens.radius.pill,
    cursor: "pointer",
    fontSize: tokens.font.size.md,
    fontWeight: tokens.font.weight.medium,
    color: tokens.text.secondary,
    fontFamily: "inherit",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  },
  iconWrap: {
    width: 28,
    height: 28,
    borderRadius: tokens.radius.sm,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
};

function QuestionIcon({ type }: { type: PopularQuestion["icon"] }) {
  if (type === "robot") {
    return (
      <span style={{ ...styles.iconWrap, background: tokens.icon.robotBg }}>
        <AvatarIcon variant="orange" size={18} />
      </span>
    );
  }

  if (type === "sparkles") {
    return (
      <span style={{ ...styles.iconWrap, background: tokens.icon.sparklesBg }}>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M12 3l1.2 4.2L17.5 8.5 13.2 9.8 12 14l-1.2-4.2L6.5 8.5l4.3-1.3L12 3Z"
            fill={tokens.icon.accentWarm}
          />
          <path
            d="M18 13l.7 2.3L21 16l-2.3.7L18 19l-.7-2.3L15 16l2.3-.7L18 13Z"
            fill={tokens.icon.accentWarm}
          />
        </svg>
      </span>
    );
  }

  if (type === "target") {
    return (
      <span style={{ ...styles.iconWrap, background: tokens.icon.targetBg }}>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="12"
            cy="12"
            r="7"
            stroke={tokens.icon.accent}
            strokeWidth="2"
          />
          <circle
            cx="12"
            cy="12"
            r="3.5"
            stroke={tokens.icon.accent}
            strokeWidth="2"
          />
          <circle cx="12" cy="12" r="1.2" fill={tokens.icon.accent} />
        </svg>
      </span>
    );
  }

  return (
    <span style={{ ...styles.iconWrap, background: tokens.icon.rocketBg }}>
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M5 15c4-1 6-3 7-7 1 4 3 6 7 7-4 1-6 3-7 7-1-4-3-6-7-7Z"
          fill={tokens.icon.accent}
        />
      </svg>
    </span>
  );
}

export default function PopularQuestions({
  onSelectQuestion,
  disabled,
}: PopularQuestionsProps) {
  const { t } = useTranslation();

  return (
    <section style={styles.section} aria-label={t("popular.ariaLabel")}>
      <h2 style={styles.greeting}>{t("chat.greeting")}</h2>
      <ul style={styles.list}>
        {QUESTIONS.map((q) => {
          const label = t(q.labelKey);
          return (
            <li key={q.id}>
              <button
                type="button"
                style={{
                  ...styles.item,
                  opacity: disabled ? 0.55 : 1,
                  cursor: disabled ? "not-allowed" : "pointer",
                }}
                disabled={disabled}
                onClick={() => onSelectQuestion(label)}
              >
                <QuestionIcon type={q.icon} />
                <span>{label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
